import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { ApiRoutes, AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { CloudflareAccount, CloudflareAccountsFormFields, CloudflareAccountsFormInputs, useEditCloudflareAccountsMutation, useGetByIdCloudflareAccountsQuery } from '@/modules/DomainsApi';
import { DomainsApiProviders } from '@/modules/DomainsApi/DomainsApi';

import { CloudflareAccountsFormView } from './CloudflareAccountsFormView';
import { cloudflareAccountsFormValidationSchema } from './validation';

type Props = {
  cloudflareAccountId: string;
};

export const CloudflareAccountsEditFormContainer: FC<Props> = memo(({ cloudflareAccountId }) => {
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<CloudflareAccountsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof CloudflareAccountsFormInputs>>(null);

  const {
    data: cloudflareAccountData,
    error: cloudflareAccountError,
    isLoading: isCloudflareAccountLoading,
    isError: isCloudflareAccountError
  } = useGetByIdCloudflareAccountsQuery(cloudflareAccountId)({
    enabled: !!cloudflareAccountId
  });

  const { mutate: editDomainRegistrarMutation, isLoading: editDomainRegistrarsIsLoading } =
  useEditCloudflareAccountsMutation(cloudflareAccountId)({
    onSuccess: (data: CloudflareAccount) => {
      appMessageApi.success('Cloudflare акаунт успішно змінено!');

      navigate(AppRoutes.DomainsApiCloudflareAccountsById.replace(':cloudflareAccountId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof CloudflareAccountsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: CloudflareAccountsFormFields = {
    email: cloudflareAccountData?.email,
    password: cloudflareAccountData?.password,
    apiKey: cloudflareAccountData?.apiKey,
    provider: DomainsApiProviders.Cloudflare,
  };

  const handleFinish = useCallback((values: CloudflareAccountsFormFields): void => {
    setFormErrors(null);
    editDomainRegistrarMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(ApiRoutes.DomainsApiCloudflareAccountsById.replace(':cloudflareAccountId', cloudflareAccountId));
  }, [cloudflareAccountId]);

  return (
    <FallbackedLayout
      isLoading={ isCloudflareAccountLoading }
      isError={ isCloudflareAccountError }
      error={ cloudflareAccountError }
    >
      { () => (
        <CloudflareAccountsFormView
          formName="cloudflare-account-edit"
          formType="edit"
          form={ form }
          formErrors={ formErrors }
          initialValues={ initialValues }
          isFormLoading={ editDomainRegistrarsIsLoading }
          validationSchema={ cloudflareAccountsFormValidationSchema }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) }
    </FallbackedLayout>
  );
});
